import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toApiError } from '@/api/client';
import { modeToJobType, spreadIdToLayout } from '@/api/mappers';
import { queryKeys } from '@/api/queryClient';
import { BackButton } from '@/components/common/BackButton';
import { DesktopSpreadAside } from '@/components/spread/DesktopSpreadAside';
import { SpreadForm } from '@/components/spread/SpreadForm';
import { useReadingDraft } from '@/context/ReadingDraftContext';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { useCreateJob } from '@/hooks/api/useReadings';
import { useToast } from '@/hooks/useToast';
import { t } from '@/i18n';
import anim from '@/styles/animations.module.css';
import styles from './SpreadPage.module.css';
import type { SpreadMode, SpreadTypeId } from '@/types';

function parseMode(raw: string | null): SpreadMode | null {
  if (raw === 'photo-reading' || raw === 'person-photo' || raw === 'generate') return raw;
  return null;
}

export function SpreadPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMode = parseMode(searchParams.get('mode'));
  const createJob = useCreateJob();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const onApiError = useApiErrorToast();
  const { clearDraft } = useReadingDraft();

  // The session may already be dead when this page is opened (no request has
  // happened yet to reveal it). Check now, before the user invests effort
  // filling the form, rather than only discovering it when they hit submit.
  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.me,
      refetchType: 'active',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (payload: {
    mode: SpreadMode;
    spreadTypeId: SpreadTypeId;
    question: string;
    photo: File | null;
  }) => {
    try {
      const result = await createJob.mutateAsync({
        jobType: modeToJobType[payload.mode],
        layoutType: spreadIdToLayout[payload.spreadTypeId],
        userQuestion: payload.question.trim() || undefined,
        image: payload.photo,
      });
      clearDraft();
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] });
      navigate(`/loading?jobId=${encodeURIComponent(result.jobId)}`, { replace: true });
    } catch (err) {
      const apiErr = toApiError(err);
      if (apiErr.status === 401) {
        // AuthContext's unauthorized listener already clears the session and
        // RequireAuth will redirect; the draft survives that (ReadingDraftContext
        // lives above the router). Show the real "session expired" message
        // instead of a generic "creation failed" — a custom fallback here
        // would otherwise win over the correct per-status default.
        onApiError(err);
        return;
      }
      if (apiErr.status === 402) {
        showToast(t.spread.insufficientBalance, 'error');
        navigate('/profile');
        return;
      }
      if (apiErr.status === 429) {
        showToast(t.spread.rateLimited, 'error');
        return;
      }
      onApiError(err, t.spread.createFailed);
    }
  };

  const form = (
    <SpreadForm
      initialMode={initialMode ?? undefined}
      submitting={createJob.isPending}
      onSubmit={handleSubmit}
    />
  );

  return (
    <div className={anim.fadeIn}>
      {isDesktop ? (
        <div className={styles.desktopGrid}>
          <div className={styles.main}>
            <h1 className={styles.title}>{t.spread.title}</h1>
            <section className={styles.sec}>{form}</section>
          </div>
          <DesktopSpreadAside />
        </div>
      ) : (
        <>
          <header className={styles.mobileHero}>
            <BackButton onClick={() => navigate(-1)} />
            <h1 className={styles.mobileTitle}>{t.spread.selectTitle}</h1>
          </header>
          <section className={styles.sec}>{form}</section>
        </>
      )}
    </div>
  );
}
